import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Customized,
} from 'recharts';

const AXIS_TICK_COLOR = '#555';
const EVENT_LABEL_SIZE = 13;
const IRAN_WAR_DATE = '2026-02-28';

const GOOGLE_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1JjQrNv_1SG05Vb99gOGoc9IIz9x5lNGn4GkvFozydlk';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Dark blue (nearest quarter) → light blue (furthest) — for chart lines
const Q_COLORS = ['#0a2f7a', '#1460F9', '#5a9cff', '#a8c8ff'];
// Brighter versions for dark tooltip background
const Q_TOOLTIP_COLORS = ['#6b9fff', '#88b4ff', '#aaccff', '#cce0ff'];

const TIME_RANGES = [
  { key: '1m', label: '1M', days: 30 },
  { key: '3m', label: '3M', days: 90 },
  { key: '6m', label: '6M', days: 180 },
  { key: '1y', label: '1Y', days: 365 },
  { key: 'custom', label: 'Custom', days: null },
];

const DEFAULT_RANGE = { key: 'custom', days: null, startDate: '2026-01-01' };

function filterByRange(data, range) {
  if (range.days === Infinity) return data;
  if (range.key === 'custom' && range.startDate) {
    return data.filter(d => d.date >= range.startDate && (!range.endDate || d.date <= range.endDate));
  }
  if (range.days) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - range.days);
    return data.filter(d => d.date >= cutoff.toISOString().split('T')[0]);
  }
  return data;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00Z');
  return `${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00Z');
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${days[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// Map monthly contracts to quarters
// e.g. ["Apr 26","May 26","Jun 26","Jul 26",...,"Mar 27"] → ["Q2 26","Q3 26","Q4 26","Q1 27"]
function monthsToQuarters(contracts) {
  const qMap = {}; // "Q2 26" → ["Apr 26", "May 26", "Jun 26"]
  for (const c of contracts) {
    // Parse "Apr 26" → month index 3, year "26"
    const monthIdx = MONTH_NAMES.indexOf(c.split(' ')[0]);
    const yr = c.split(' ')[1];
    if (monthIdx < 0) continue;
    const qNum = Math.floor(monthIdx / 3) + 1;
    const qKey = `Q${qNum} ${yr}`;
    if (!qMap[qKey]) qMap[qKey] = [];
    qMap[qKey].push(c);
  }
  // Include quarters with at least 2 months of data
  return Object.entries(qMap)
    .filter(([, months]) => months.length >= 2)
    .map(([q, months]) => ({ quarter: q, months }));
}

function FuturesTooltip({ active, payload, label, quarters, colors, unit }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const fmtVal = (v) => unit === 'ptherm' ? `${Math.round(v)} p/therm` : `£${Math.round(v)}/MWh`;

  return (
    <div className="wh-tooltip">
      <p className="wh-tooltip-date">{formatDateLong(label)}</p>
      {quarters.map((q, i) => {
        const val = point[q.quarter];
        if (val == null) return null;
        return (
          <p key={q.quarter} style={{ color: colors?.[i] || Q_TOOLTIP_COLORS[i], fontWeight: 600, margin: '2px 0' }}>
            {q.quarter}: {fmtVal(val)}
          </p>
        );
      })}
    </div>
  );
}

function TimeRangeSelector({ selected, onChange, style }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(selected.startDate || '');
  const [customEnd, setCustomEnd] = useState(selected.endDate || '');
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!showCustom) return;
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowCustom(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCustom]);

  return (
    <div className="wh-time-range" style={{ position: 'relative', ...style }}>
      {TIME_RANGES.map(r => (
        <button
          key={r.key}
          className={`wh-range-btn ${selected.key === r.key ? 'active' : ''}`}
          onClick={() => {
            if (r.key === 'custom') {
              setShowCustom(!showCustom);
            } else {
              onChange(r);
              setShowCustom(false);
            }
          }}
        >{r.label}</button>
      ))}
      {showCustom && (
        <div className="wh-custom-picker" ref={pickerRef}>
          <label>From <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} /></label>
          <label>To <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} /></label>
          <button className="wh-custom-apply" onClick={() => {
            onChange({ key: 'custom', days: null, startDate: customStart, endDate: customEnd || undefined });
            setShowCustom(false);
          }}>Apply</button>
        </div>
      )}
    </div>
  );
}

// Render quarter labels in the right margin, positioned at each line's last Y value
function QuarterLabels({ yAxisMap, offset, quarters, filteredData, colors }) {
  const yAxis = yAxisMap && Object.values(yAxisMap)[0];
  if (!yAxis || !filteredData.length) return null;

  const labelX = offset.left + offset.width + 6;
  const lastPoint = filteredData[filteredData.length - 1];
  const minGap = 15;

  const labels = quarters
    .map((q, i) => {
      const val = lastPoint[q.quarter];
      if (val == null) return null;
      return { quarter: q.quarter, py: yAxis.scale(val), color: (colors || Q_COLORS)[i] };
    })
    .filter(Boolean)
    .sort((a, b) => a.py - b.py);

  // Nudge overlapping labels downward
  for (let i = 1; i < labels.length; i++) {
    if (labels[i].py - labels[i - 1].py < minGap) {
      labels[i].py = labels[i - 1].py + minGap;
    }
  }
  // If pushed below chart, shift entire stack up
  const chartBottom = offset.top + offset.height;
  if (labels.length > 0 && labels[labels.length - 1].py > chartBottom - 5) {
    const overshoot = labels[labels.length - 1].py - (chartBottom - 5);
    for (const l of labels) l.py -= overshoot;
  }

  return (
    <g>
      {labels.map(l => (
        <text
          key={l.quarter}
          x={labelX}
          y={l.py}
          fill={l.color}
          fontSize={11}
          fontWeight={600}
          dominantBaseline="middle"
        >
          {l.quarter}
        </text>
      ))}
    </g>
  );
}

// fuel: "electricity" | "gas"
export default function FuturesChart({ futuresData, fuel = 'electricity' }) {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [gasUnit, setGasUnit] = useState('ptherm');
  const [elecProduct, setElecProduct] = useState('base');

  const isGas = fuel === 'gas';

  // Build quarterly chart data
  const { chartData, quarters } = useMemo(() => {
    if (isGas) {
      // Gas: use quarterly strips if available, otherwise average monthly contracts
      if (!futuresData?.gas) return { chartData: [], quarters: [] };
      const { data, contracts: allContracts } = futuresData.gas;
      const qContracts = allContracts.filter(c => c.startsWith('Q'));

      let qs;
      if (qContracts.length >= 4) {
        // Quarterly strips available — use directly, limit to 4
        qs = qContracts.slice(0, 4).map(c => ({ quarter: c, months: [] }));
      } else {
        // Fallback: average monthly contracts into quarters, limit to 4
        const monthlyContracts = allContracts.filter(c => !c.startsWith('Q') && !c.startsWith('S') && !c.startsWith('W'));
        qs = monthsToQuarters(monthlyContracts.slice(0, 12)).slice(0, 4);
      }

      const dates = Object.keys(data).sort();
      const rows = dates.map(date => {
        const row = { date };
        for (const q of qs) {
          if (q.months.length === 0) {
            // Direct quarterly strip
            if (data[date]?.[q.quarter] != null) row[q.quarter] = data[date][q.quarter];
          } else {
            // Average monthly contracts
            const vals = q.months.map(m => data[date]?.[m]).filter(v => v != null);
            if (vals.length > 0) {
              row[q.quarter] = +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
            }
          }
        }
        return row;
      });
      return { chartData: rows, quarters: qs };
    } else {
      // Electricity: aggregate monthly contracts into quarterly averages
      const source = elecProduct === 'peak' ? futuresData?.peak : futuresData?.base;
      if (!source) return { chartData: [], quarters: [] };
      const { data, contracts: allContracts } = source;
      const dates = Object.keys(data).sort();
      const active = allContracts.slice(0, 12);
      const qs = monthsToQuarters(active);
      const rows = dates.map(date => {
        const row = { date };
        for (const q of qs) {
          const vals = q.months.map(m => data[date]?.[m]).filter(v => v != null);
          if (vals.length > 0) {
            row[q.quarter] = +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
          }
        }
        return row;
      });
      return { chartData: rows, quarters: qs };
    }
  }, [futuresData, isGas, elecProduct]);

  // Gas unit conversion
  const gasConv = isGas && gasUnit === 'mwh' ? (10 / 29.3071) : 1;
  const convertedData = useMemo(() => {
    if (gasConv === 1) return chartData;
    return chartData.map(row => {
      const out = { date: row.date };
      for (const q of quarters) {
        if (row[q.quarter] != null) out[q.quarter] = +(row[q.quarter] * gasConv).toFixed(2);
      }
      return out;
    });
  }, [chartData, quarters, gasConv]);

  const filteredData = useMemo(() => filterByRange(convertedData, range), [convertedData, range]);

  const eventLines = useMemo(() => {
    if (filteredData.length === 0) return [];
    const minDate = filteredData[0].date;
    const maxDate = filteredData[filteredData.length - 1].date;
    if (IRAN_WAR_DATE < minDate || IRAN_WAR_DATE > maxDate) return [];
    const dates = filteredData.map(d => d.date);
    let closest = dates[0], minDiff = Infinity;
    for (const d of dates) {
      const diff = Math.abs(new Date(d) - new Date(IRAN_WAR_DATE));
      if (diff < minDiff) { minDiff = diff; closest = d; }
    }
    return [{ date: closest, label: 'Iran war' }];
  }, [filteredData]);

  const handleRangeChange = useCallback((r) => setRange(r), []);

  if (!futuresData || chartData.length === 0) return null;

  const unit = isGas ? gasUnit : 'mwh';
  const fmtTick = unit === 'ptherm' ? (v => `${Math.round(v)}p`) : (v => `£${v}`);
  const unitLabel = unit === 'ptherm' ? 'p/therm' : '£/MWh';
  const colors = isGas
    ? ['#c45e1a', '#f07856', '#f5a88a', '#f9d4c4']
    : Q_COLORS;
  const tooltipColors = isGas
    ? ['#ffb088', '#ffcaaa', '#ffdcc8', '#ffede0']
    : Q_TOOLTIP_COLORS;

  return (
    <section className="wh-section">
      <div className="wh-section-header">
        <h2>{isGas ? 'Gas' : 'Electricity'} Futures</h2>
        <span className="wh-section-subtitle">
          {isGas
            ? 'Daily prices of UK NBP quarterly forward contracts'
            : `Daily prices of UK ${elecProduct === 'peak' ? 'peakload' : 'baseload'} forward contracts`}
        </span>
      </div>

      <div className="wh-chart-card">
        <div className="wh-chart-controls">
          <div className="wh-controls-row">
            <div className="wh-left-controls">
              <TimeRangeSelector selected={range} onChange={handleRangeChange} />
              {!isGas && (
                <div className="wh-secondary-controls">
                  <div className="wh-price-mode-toggle">
                    <button
                      className={`wh-range-btn ${elecProduct === 'base' ? 'active' : ''}`}
                      onClick={() => setElecProduct('base')}
                    >Base</button>
                    <button
                      className={`wh-range-btn ${elecProduct === 'peak' ? 'active' : ''}`}
                      onClick={() => setElecProduct('peak')}
                    >Peak</button>
                  </div>
                </div>
              )}
              {isGas && (
                <div className="wh-secondary-controls">
                  <div className="wh-granularity-toggle">
                    <button
                      className={`wh-range-btn ${gasUnit === 'ptherm' ? 'active' : ''}`}
                      onClick={() => setGasUnit('ptherm')}
                    >p/therm</button>
                    <button
                      className={`wh-range-btn ${gasUnit === 'mwh' ? 'active' : ''}`}
                      onClick={() => setGasUnit('mwh')}
                    >£/MWh</button>
                  </div>
                </div>
              )}
            </div>
            <div className="wh-mode-selector">
              <a
                href={GOOGLE_SHEET_URL}
                target="_blank"
                rel="noreferrer"
                className="wh-download-btn"
                title="Open data in Google Sheets"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={filteredData}
            margin={{ top: 10, right: 65, left: 10, bottom: 0 }}
          >
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
              axisLine={{ stroke: '#ddd' }}
              tickLine={false}
              minTickGap={40}
            />
            <YAxis
              tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
              axisLine={{ stroke: '#eee' }}
              tickLine={false}
              tickFormatter={fmtTick}
              width={55}
              domain={[0, 'auto']}
              label={{ value: unitLabel, angle: -90, position: 'insideLeft', offset: 5, style: { fill: '#888', fontSize: 12 } }}
            />
            <Tooltip content={<FuturesTooltip quarters={quarters} colors={tooltipColors} unit={unit} />} />

            {quarters.map((q, i) => (
              <Line
                key={q.quarter}
                type="linear"
                dataKey={q.quarter}
                stroke={colors[i]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3, fill: colors[i] }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}

            {eventLines.map(e => (
              <ReferenceLine
                key={e.date}
                x={e.date}
                stroke="#aaa"
                strokeDasharray="6 4"
                strokeWidth={1}
                label={{ value: e.label, position: 'insideTopLeft', fill: AXIS_TICK_COLOR, fontSize: EVENT_LABEL_SIZE }}
              />
            ))}

            <Customized
              component={(props) => (
                <QuarterLabels {...props} quarters={quarters} filteredData={filteredData} colors={colors} />
              )}
            />
          </LineChart>
        </ResponsiveContainer>

        <div style={{ textAlign: 'right', fontSize: '0.75em', color: '#aaa', marginTop: 2, paddingRight: 4 }}>
          Source: ICE {isGas ? 'UK Natural Gas' : 'UK Electricity'} Futures
        </div>
      </div>
    </section>
  );
}
